"use client";
import { useState } from "react";
import Image from "next/image";
import { Images, Icons } from "@/app/assets/shared";
import PrimaryBtn from "@/components/PrimaryBtn";
import { useIsMobile } from "@/hooks/useMediaQuery";
import { CLIENT_ENTRIES, ClientEntry } from "./data";

const ChallengeIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0Z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

const SolutionIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M9 18h6" />
    <path d="M10 22h4" />
    <path d="M15.09 14c.18-.98.65-1.74 1.41-2.5A4.65 4.65 0 0 0 18 8 6 6 0 0 0 6 8c0 1 .23 2.23 1.5 3.5.75.75 1.23 1.5 1.41 2.5" />
  </svg>
);

const ImpactIcon = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#0a0a0a"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
  </svg>
);

export default function SuccessStoryShowcase({
  clients = CLIENT_ENTRIES,
}: {
  clients?: ClientEntry[];
}) {
  const isMobile = useIsMobile();
  const [activeClientId, setActiveClientId] = useState(clients[0]?.id);

  const activeClient =
    clients.find((client) => client.id === activeClientId) ?? clients[0];
  const caseStudy = activeClient?.caseStudy;

  return (
    <section className="success-story-showcase relative w-full overflow-hidden bg-[#000000]">
      {/* Texture background */}
      <div className="texture-bg pointer-events-none absolute left-0 top-0 z-0 h-full w-full mix-blend-overlay">
        {!isMobile && (
          <div className="absolute inset-0 z-0">
            <Image
              src={Images?.texture}
              alt="texture"
              fill
              className="object-cover mix-blend-overlay opacity-60"
              sizes="(max-width: 768px) 100vw, (max-width: 1536px) 50vw, 700px"
            />
          </div>
        )}
      </div>

      {/* Glow accents */}
      <div className="absolute right-0 top-0 h-[907px] w-[907px] -translate-y-1/3 translate-x-1/3 rounded-full bg-[#225bd4]/40 blur-[333px]" />
      <div className="absolute bottom-0 left-0 h-[907px] w-[907px] -translate-x-1/3 translate-y-1/3 rounded-full bg-[#225bd4]/40 blur-[333px]" />

      <div className="relative z-[1] mx-auto w-[calc(100%_-_10%)]  py-[50px] lg:py-[100px]">
        {/* Header */}
        <div className="flex flex-col items-center gap-4 text-center">
          <p className="text-md font-medium uppercase tracking-tight text-white">
            Our Success Stories
          </p>
          <h2 className="clash-font max-w-[24ch] text-[length:var(--section-title)] font-medium leading-[110%]">
            <span className="text-white">Real Result. Real Impact. </span>
            <span className="text-[#225BD4]">Real Stories Delivered.</span>
          </h2>
          <p className="max-w-[62ch] text-[length:var(--hero-para)] font-normal leading-[150%] text-white/50">
            Our work speaks louder than words. We collaborate with visionary
            brands to solve complex challenges and deliver measurable outcomes.
          </p>
        </div>

        {/* Outer #ECF0F6 box */}
        {caseStudy && (
          <div className="mt-10 w-full rounded-none bg-[#ECF0F6] p-3 lg:mt-16 lg:p-8">
            {/* Inner white content box */}
            <div className="flex w-full flex-col overflow-hidden rounded-none bg-white lg:flex-row">
              {/* Left: content - 60% */}
              <div className="flex w-full flex-col gap-6 p-8 lg:w-[60%] lg:p-12">
                <h3 className="clash-font text-2xl font-medium leading-[120%] text-[#0a0a0a] lg:text-4xl">
                  {caseStudy.title}
                </h3>

                {/* Tags */}
                {caseStudy.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {caseStudy.tags.map((tag) => (
                      <span
                        key={tag.id}
                        className="rounded-full bg-[#ECF0F6] px-4 py-2 text-[15px] font-medium text-[#00040B]"
                      >
                        {tag.name}
                      </span>
                    ))}
                  </div>
                )}

                {/* Challenge */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <ChallengeIcon />
                    <p className="text-[20px] font-semibold text-[#0a0a0a]">
                      The Challenge
                    </p>
                  </div>

                  <p className="pl-7 text-[18px] leading-[160%] text-[#5a5a5a]">
                    {caseStudy.challenge}
                  </p>
                </div>

                {/* Solution */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <SolutionIcon />
                    <p className="text-[20px] font-semibold text-[#0a0a0a]">
                      The Solution
                    </p>
                  </div>

                  <p className="pl-7 text-[18px] leading-[160%] text-[#5a5a5a]">
                    {caseStudy.solution}
                  </p>
                </div>

                {/* Impact */}
                <div className="flex flex-col gap-2">
                  <div className="flex items-center gap-2">
                    <ImpactIcon />
                    <p className="text-[20px] font-semibold text-[#0a0a0a]">
                      The Impact
                    </p>
                  </div>

                  <ul className="inline-grid grid-cols-[max-content_max-content] gap-x-12 gap-y-1 pl-7 text-[18px] leading-[160%] text-[#5a5a5a]">
                    {caseStudy.impact.map((point) => (
                      <li
                        key={point.id}
                        className="list-disc marker:text-[#5a5a5a]"
                      >
                        {point.text}
                      </li>
                    ))}
                  </ul>
                </div>

                {/* CTA — opens/downloads the case-study PDF for this client */}
                <div className="mt-2 w-fit">
                  <a
                    href={caseStudy.caseStudyHref}
                    target="_blank"
                    rel="noopener noreferrer"
                    download
                  >
                    <PrimaryBtn
                      text="View Case Study"
                      iconSrc={Icons?.chevron}
                      dark={false}
                    />
                  </a>
                </div>
              </div>

              {/* Right: image - 40% */}
              <div className="relative w-full lg:w-[40%]">
                <div className="relative aspect-[4/3] w-full lg:aspect-auto lg:h-full">
                  {caseStudy.image && (
                    <Image
                      src={caseStudy.image}
                      alt={caseStudy.title}
                      fill
                      sizes="(max-width: 1024px) 100vw, 40vw"
                      className="object-cover"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Client logos row — click to switch active case study */}
        {clients?.length > 0 && (
          <div className="mt-10 flex flex-wrap items-center justify-center gap-3">
            {clients.map((client) => {
              const isActive = client.id === activeClientId;
              return (
                <button
                  key={client.id}
                  type="button"
                  onClick={() => setActiveClientId(client.id)}
                  aria-pressed={isActive}
                  aria-label={client.name}
                  className={`flex h-[54px] items-center justify-center rounded-full px-8 transition-colors ${
                    isActive
                      ? "bg-[#225BD4]"
                      : "border border-white/20 bg-white/5 hover:bg-white/10"
                  }`}
                >
                  {client.logo ? (
                    <Image
                      src={client.logo}
                      alt={client.name}
                      width={120}
                      height={30}
                      className={`h-[26px] lg:h-[30px] w-auto object-contain transition-opacity ${
                        isActive ? "opacity-100" : "opacity-50 hover:opacity-80"
                      }`}
                    />
                  ) : (
                    <span
                      className={`text-sm font-medium ${
                        isActive ? "text-white" : "text-white/40"
                      }`}
                    >
                      {client.name}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>
    </section>
  );
}
